import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Eye, Tag, Clock } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { htmlToPlainText } from '@/lib/richText';

const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await addToCart(product.id);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      print: 'Print',
      digital: 'Digital',
      social: 'Social',
      advertorial: 'Advertorial',
      events: 'Events'
    };
    return labels[category] || category;
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const isOnSale = product.is_on_sale && product.discount_valid;
  const discountPercentage = product.discount_percentage;
  const expiryDate = formatExpiryDate(product.discount_expires_at);

  return (
    <Card 
      className={`group relative overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:shadow-lg hover:border-navy-200 product-card ${isOnSale ? 'ring-2 ring-red-500/20' : ''}`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Category badge */}
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-900 text-white">
              {getCategoryLabel(product.category)}
            </span>
          </div>
          
          {/* Sale badge */}
          {isOnSale && (
            <div className="absolute top-3 right-3">
              <Badge className="bg-red-600 text-white text-sm font-bold px-3 py-1 animate-pulse">
                <Tag className="h-3 w-3 mr-1" />
                {discountPercentage}% OFF
              </Badge>
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-navy-700 transition-colors">
            {product.name}
          </h3>
          <p className="text-sm text-slate-600 line-clamp-2 mb-4 font-body">
            {htmlToPlainText(product.description)}
          </p>
          <div className="flex flex-col gap-2">
            {/* Pricing */}
            <div className="flex items-center gap-2 flex-wrap">
              {isOnSale ? (
                <>
                  <span className="price-tag text-2xl text-red-600">
                    {formatPrice(product.final_price)}
                  </span>
                  <span className="text-lg text-slate-400 line-through">
                    {formatPrice(product.price)}
                  </span>
                </>
              ) : (
                <span className="price-tag text-2xl text-navy-900">
                  {formatPrice(product.final_price || product.price)}
                </span>
              )}
            </div>
            
            {/* Expiry notice */}
            {isOnSale && expiryDate && (
              <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full w-fit">
                <Clock className="h-3 w-3" />
                <span>Offer ends {expiryDate}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Link>
      <div className="px-4 pb-4">
        <div className="flex gap-2">
          <Button
            onClick={handleAddToCart}
            disabled={loading}
            className={`flex-1 ${isOnSale ? 'bg-red-600 hover:bg-red-700' : 'bg-navy-900 hover:bg-navy-800'} text-white`}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Add to Cart
          </Button>
          <Link to={`/product/${product.id}`}>
            <Button variant="outline" size="icon" className="border-navy-200 hover:bg-navy-50">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default ProductCard;
